// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import { useState } from 'react';
import { processBulkUpload } from '@ion/ion-experimental';
import { useInsertAnalyticsFile } from '@ion/api';
import parseYamlFile from 'app/components/analytics-builder/export-import/parseYamlFile';

const UPLOAD_STATE = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
};

const MAX_FILES = 100;

export default function useBulkUploadState({ pipelines, consentCategories }) {
  const [uploadState, setUploadState] = useState(UPLOAD_STATE.IDLE);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileCount, setFileCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [results, setResults] = useState({ createdFiles: [], failedFiles: [], succeeded: 0, failed: 0 });
  const [insertAnalyticsFile] = useInsertAnalyticsFile();

  const handleFileSelect = async event => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      try {
        const content = await file.text();
        const parsed = parseYamlFile(content);
        const count = Object.keys(parsed.files ?? {}).length;
        setFileCount(count);
        setHasError(count > MAX_FILES || count === 0);
      } catch (error) {
        setFileCount(0);
        setHasError(true);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || hasError) return;

    setUploadState(UPLOAD_STATE.PROCESSING);

    const fileContent = await selectedFile.text();
    const { createdFiles, failedFiles } = await processBulkUpload({
      fileContent,
      pipelines,
      consentCategories,
      insertAnalyticsFile,
    });

    setResults({
      createdFiles,
      failedFiles,
      succeeded: createdFiles.length,
      failed: failedFiles.length,
    });
    setUploadState(UPLOAD_STATE.COMPLETE);
  };

  const reset = () => {
    setUploadState(UPLOAD_STATE.IDLE);
    setSelectedFile(null);
    setFileCount(0);
    setHasError(false);
    setResults({ createdFiles: [], failedFiles: [], succeeded: 0, failed: 0 });
  };

  return {
    uploadState,
    selectedFile,
    fileCount,
    hasError,
    results,
    isIdle: uploadState === UPLOAD_STATE.IDLE,
    isProcessing: uploadState === UPLOAD_STATE.PROCESSING,
    isComplete: uploadState === UPLOAD_STATE.COMPLETE,
    handleFileSelect,
    handleUpload,
    reset,
  };
}
